import styled from "styled-components"
import { useLocale, useT } from "react-polyglot-hooks"
import * as PropTypes from "prop-types"
import Image from "next/legacy/image"
import { mq } from "helpers/styled"
import Typography from "components/styled/Typography"
import Icon from "components/icons/icon"
import { CarDoor } from "components/icons/carDoor"
import { Charging } from "components/icons/charging"
import { Engine } from "components/icons/engine"
import { GasStation } from "components/icons/gasStation"
import { Leaf } from "components/icons/leaf"
import { Gearshift } from "components/icons/gearShift"
import { Transmission } from "components/icons/transmission"
import { Delivery } from "components/icons/delivery"

const Grid = styled.div`
  --grid-count: 3;

  display: grid;
  grid-template-columns: repeat(var(--grid-count), minmax(10px, 1fr));
  gap: 25px 0;
  margin-bottom: 25px;

  @media ${mq("md")} {
    --grid-count: 6;
  }
`

const Item = styled.div`
  padding: 0;
  text-align: center;
`

const Label = styled(Typography)`
  text-transform: uppercase;
  font-size: 11px;
  white-space: nowrap;
  margin-top: 10px;
`

const Text = styled(Typography)`
  text-transform: capitalize;
  white-space: nowrap;

  > span {
    text-transform: lowercase;
  }
`

export default function FeaturesList({ car }) {
  const t = useT()
  const locale = useLocale()

  const { fuel, transmission, environmentalLabel, cv, doors, co2Emission, deliveryTime } = car

  const fuelItem = (
    <Item className="feature">
      <Icon size="lg" Icon={fuel === "electric" ? Charging : GasStation} />
      <Label variant="small" color="gray">
        {t("car_data.features.fuel")}
      </Label>
      <Text weight="bold">{t(`car.fuel.${fuel}`)}</Text>
    </Item>
  )

  const transmissionItem = (
    <Item className="feature">
      <Icon size="lg" Icon={transmission === "automatic" ? Gearshift : Transmission} />
      <Label variant="small" color="gray">
        {t("car_data.features.transmission")}
      </Label>
      <Text weight="bold">{t(`car.transmission.${transmission}`)}</Text>
    </Item>
  )

  const envLabelItem = locale === "es" && environmentalLabel && (
    <Item className="feature">
      <Image
        alt={`Etiqueta medioambiental ${environmentalLabel}`}
        width={32}
        height={32}
        src={`/f_svg/static/etiquetas/dgt-${environmentalLabel === '0' ? 'zero' : environmentalLabel}`}
      />
      <Label variant="small" color="gray">
        {t("car_data.features.environmental_label")}
      </Label>
      <Text weight="bold">
        {environmentalLabel === "zero" ? t(`car.labels.${environmentalLabel}`) : environmentalLabel}
      </Text>
    </Item>
  )

  const engineItem = (
    <Item className="feature">
      <Icon size="lg" Icon={Engine} />
      <Label variant="small" color="gray">
        {t("car_data.features.engine")}
      </Label>
      <Text weight="bold">{`${cv} CV`}</Text>
    </Item>
  )

  const doorsItem = (
    <Item className="feature">
      <Icon size="lg" Icon={CarDoor} />
      <Label variant="small" color="gray">
        {t("car_data.features.doors")}
      </Label>
      <Text weight="bold">{doors}</Text>
    </Item>
  )

  const emissionItem = (
    <Item className="feature">
      <Icon size="lg" Icon={Leaf} />
      <Label variant="small" color="gray">
        {t("car_data.features.emission")}
      </Label>
      <Text weight="bold">{`${co2Emission !== null && co2Emission !== "" ? co2Emission : "n/a"}g CO2/Km`}</Text>
    </Item>
  )

  const deliveryItem = ["it", "pt"].includes(locale) && (
    <Item className="feature">
      <Icon viewBox="0 0 32 32" size="lg" Icon={Delivery} />
      <Label variant="small" color="gray">
        {t("car_data.features.delivery-time")}
      </Label>
      <Text weight="bold">
        {deliveryTime} <span>{t("common.weeks")}</span>
      </Text>
    </Item>
  )

  if (locale === "es")
    return (
      <Grid>
        {fuelItem}
        {transmissionItem}
        {envLabelItem}
        {!!cv && engineItem}
        {!!doors && doorsItem}
        {!!co2Emission && emissionItem}
      </Grid>
    )

  return (
    <Grid>
      {deliveryItem}
      {transmissionItem}
      {engineItem}
      {fuelItem}
      {doorsItem}
      {emissionItem}
    </Grid>
  )
}

FeaturesList.propTypes = {
  car: PropTypes.object.isRequired
}
